import { Suspense, useEffect, useRef } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
  type LoaderFunctionArgs,
} from "react-router";
import { RouteErrorElement } from "@/pages/ErrorPage";
import { Spinner } from "@/components/ui/spinner";
import { onAuthTokensChanged, tokenStorage, isTokenExpired } from "@/lib/auth";
import { useOrganizationStore } from "@/stores/organizationStore";
import { useAuthContext } from "@/hooks/useAuthContext";
import { initDevMode } from "@/lib/dev-mode";

const PUBLIC_ROUTES = new Set(["/login", "/authenticate", "/reset-password", "/admin-bridge"]);

// Portal mode and label mode have disjoint routes. Returns where a request on the
// wrong side belongs, or null when it may proceed.
function modeRedirect(pathname: string, inPortal: boolean): string | null {
  if (inPortal === pathname.startsWith("/portal")) return null;
  return inPortal ? "/portal" : "/catalog";
}

function LabelBoundary() {
  const { currentLabel, currentPayee } = useOrganizationStore();
  // What the pages are scoped to: the payee in portal mode, else the label. Two
  // payees at one label share a label id, so the payee id must drive the reset.
  const scopeId = currentPayee?.id ?? currentLabel?.id;
  const { user } = useAuthContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const prevScopeIdRef = useRef(scopeId);

  // Decided during render so the wrong side never mounts or fires its queries.
  const redirectTo = user ? modeRedirect(pathname, !!currentPayee) : null;

  useEffect(() => {
    const prevScopeId = prevScopeIdRef.current;
    prevScopeIdRef.current = scopeId;

    // A mode switch also changes the scope; the <Navigate> below owns that move.
    if (redirectTo || !prevScopeId || !scopeId || scopeId === prevScopeId) return;

    // Back to the section root: /catalog/:id -> /catalog, /portal/statements/... -> /portal/statements.
    const segments = window.location.pathname.split("/").filter(Boolean);
    const rootDepth = segments[0] === "portal" ? 2 : 1;
    if (segments.length > rootDepth) {
      navigate(`/${segments.slice(0, rootDepth).join("/")}`, { replace: true });
      return;
    }

    const params = new URLSearchParams(window.location.search);
    if (params.has("page")) {
      params.delete("page");
      const search = params.toString();
      navigate(`${window.location.pathname}${search ? `?${search}` : ""}`, {
        replace: true,
      });
    }
  }, [scopeId, navigate, redirectTo]);

  // First login: the user is known but AuthProvider's initFromUser effect has not
  // run yet, so the store is empty. Hold the page until the selection exists,
  // otherwise a payee-only user mounts /catalog for one commit.
  const hasAccess =
    !!user && (user.organizations.some((o) => o.labels?.length) || !!user.payees?.length);
  if (hasAccess && !currentLabel) return <RouteFallback />;

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return <Outlet key={scopeId} />;
}

function protectedLoader({ request }: LoaderFunctionArgs) {
  requireSession();

  // The persisted selection decides the mode before any route loader or component
  // runs, so a deep link never fetches label data as a payee.
  const redirectTo = modeRedirect(
    new URL(request.url).pathname,
    !!useOrganizationStore.getState().currentPayee,
  );
  if (redirectTo) throw redirect(redirectTo);
  return null;
}

function requireSession() {
  const accessToken = tokenStorage.getAccessToken();
  const refreshToken = tokenStorage.getRefreshToken();

  // If no tokens at all, redirect to login
  if (!accessToken && !refreshToken) {
    throw redirect("/login");
  }

  // Refresh token is the source of truth for a renewable session.
  if (refreshToken && isTokenExpired(refreshToken)) {
    tokenStorage.remove();
    throw redirect("/login");
  }

  // If access token exists and is still valid, allow navigation.
  if (accessToken && !isTokenExpired(accessToken)) {
    return;
  }

  // Access may be expired/missing; a valid refresh token can recover the session.
  if (refreshToken) {
    return;
  }

  tokenStorage.remove();
  throw redirect("/login");
}

// Dev mode has to be live BEFORE the router is created below:
// createBrowserRouter() calls .initialize() straight away at module scope, which
// runs protectedLoader on the initial URL. This module is evaluated as part of
// main.tsx's import graph, i.e. before main.tsx's own body calls initDevMode() --
// so on a first visit with an empty localStorage (incognito, a fresh preview) the
// guard read no tokens and redirected to /login before the fake session existed.
// seedDevAuth()/installDevApiMock() are both idempotent and no-ops unless the
// bypass is on, so the call in main.tsx can stay where it is.
initDevMode();

const router = createBrowserRouter([
  {
    path: "/login",
    lazy: async () => {
      const module = await import("@/pages/Login");
      return { Component: module.Login };
    },
    errorElement: <RouteErrorElement />,
  },
  {
    path: "/authenticate",
    lazy: async () => {
      const module = await import("@/pages/Authenticate");
      return { Component: module.Authenticate };
    },
    errorElement: <RouteErrorElement />,
  },
  {
    path: "/reset-password",
    lazy: async () => {
      const module = await import("@/pages/ResetPassword");
      return { Component: module.ResetPassword };
    },
    errorElement: <RouteErrorElement />,
  },
  {
    path: "/admin-bridge",
    lazy: async () => {
      const module = await import("@/pages/AdminBridge");
      return { Component: module.AdminBridge };
    },
    errorElement: <RouteErrorElement />,
  },
  {
    element: <LabelBoundary />,
    children: [
      {
        path: "/",
        loader: (args) => {
          protectedLoader(args);
          return redirect("/catalog");
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/labels",
        lazy: async () => {
          const module = await import("@/pages/Labels");
          return {
            Component: module.Labels,
            loader: async (args) => {
              protectedLoader(args);
              return module.loader();
            },
          };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/catalog",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/Catalog");
          return { Component: module.Catalog };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/catalog/import",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/CatalogImport");
          return { Component: module.CatalogImport };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/catalog/:release_id",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/ReleaseDetails");
          return { Component: module.ReleaseDetails };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/artists/:contributor_id",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/ArtistDetails");
          return { Component: module.ArtistDetails };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/artists",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/Artists");
          return { Component: module.Artists };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/contracts",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/Contracts");
          return { Component: module.Contracts };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/payees",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/Payees");
          return { Component: module.Payees };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/contracts/new",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/ContractBuilder");
          return { Component: module.ContractBuilder };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        // Editing a contract, in the same full-screen builder.
        path: "/contracts/:contract_id/edit",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/ContractBuilder");
          return { Component: module.ContractBuilder };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/contracts/new-sub-contract",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/SubContractBuilder");
          return { Component: module.SubContractBuilder };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/contracts/:contract_id",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/ContractDetails");
          return { Component: module.ContractDetails };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/payees/:payee_id",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/PayeeDetails");
          return { Component: module.PayeeDetailsPage };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/revenue",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/Revenue");
          return { Component: module.Revenue };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/revenue/import",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/RevenueImport");
          return { Component: module.RevenueImport };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/revenue/:statement_id",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/RevenueStatementDetails");
          return { Component: module.RevenueStatementDetails };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/expenses",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/Expenses");
          return { Component: module.Expenses };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/royalty-statements/create",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/CreateRoyaltyStatementPage");
          return { Component: module.CreateRoyaltyStatementPage };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/royalty-statements",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/RoyaltyStatements");
          return { Component: module.RoyaltyStatements };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/search",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/SearchResults");
          return { Component: module.SearchResults };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/chart-of-accounts",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/ChartOfAccounts");
          return { Component: module.ChartOfAccounts };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/journal-entries",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/JournalEntries");
          return { Component: module.JournalEntries };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/settings",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/Settings");
          return { Component: module.Settings };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/portal",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/portal/PortalOverview");
          return { Component: module.PortalOverview };
        },
        errorElement: <RouteErrorElement />,
      },
      {
        path: "/portal/statements",
        loader: protectedLoader,
        lazy: async () => {
          const module = await import("@/pages/portal/PortalStatements");
          return { Component: module.PortalStatements };
        },
        errorElement: <RouteErrorElement />,
      },
    ],
  },
]);

export default function AppRoutes() {
  useEffect(() => {
    return onAuthTokensChanged(() => {
      const accessToken = tokenStorage.getAccessToken();
      const refreshToken = tokenStorage.getRefreshToken();

      if (!accessToken && !refreshToken && !PUBLIC_ROUTES.has(window.location.pathname)) {
        void router.navigate("/login");
      }
    });
  }, []);

  return (
    <Suspense fallback={<RouteFallback />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}

function RouteFallback() {
  return (
    <div className="flex h-full w-full items-center justify-center p-8">
      <Spinner />
    </div>
  );
}
